import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { App, Button, Checkbox, Flex, Form, Input, Modal, Steps, InputNumber, Tabs, Select, notification } from "antd";
import { ProductSelectMulti } from "@/components/Selector";
import { RequirementColumn } from "@/pages/Requirement/type";
import { RequirementAddItem, RequirementAddItemUseRepl } from "@/types/requirement";
import { Product } from "@/types/product";
import MatchStock from "../../../Create/components/MatchStock";
import { MatchedRequirementColumn } from "../../../Create";
import PreOutData from "../../../Create/components/PreOutData";
import { itemAdds, inventoryOrders } from "@/apis/requirement";
import { RequirementDetailType } from "../../hooks/useGetDetail";
import SText from "@/styles/SText";
import { ColumnsType } from "antd/es/table";
import { ChangeEnum, OperateEnum, ConfirmedEnum } from "@/types/inventory";
import { SourceTypeEnum } from "@/types/common";
import { orderAdd, orderDescUpdate, orderItemAdds, orderPreOut } from "@/apis/inventoryv2";
import { OrderAddV2, OrderAddItemV2, OrderV2 } from "@/types/inventoryV2";
import { numFormmater } from "@/pages/Requirement/util";
import SelectLocationType from "@/components/Selector/SelectLocationType";
import { AddFactoryInventory, FactoryInventorySourceType } from "@/types/factoryInventory";
import { fInventoryAdjust } from "@/apis/factoryInventory";
import CommonTable from "@/components/CommonTable";
import errorCodes from "@/apis/error";
import { roundToDecimal } from "@/utils/calc";

interface ProductWithNum extends Product {
  quantity?: number;
  bomAmount?: number;
}

interface AddItemProps {
  open: boolean;
  setOpen: (flag: boolean) => void;
  reqId: string;
  onSuccess?: (
    newItems: RequirementDetailType[],
    orderChanges?: {
      updatedOrder?: OrderV2;
      createdOrder?: OrderV2;
    }
  ) => void;
}

const AddItem: FC<AddItemProps> = ({
  open,
  setOpen,
  reqId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();

  const contentRef = useRef<HTMLDivElement>(null);

  const [current, setCurrent] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [openProductSelect, setOpenProductSelect] = useState<boolean>(false);
  const [selectedProducts, setSelectedProducts] = useState<ProductWithNum[]>([]);
  const [matchedProducts, setMatchedProducts] = useState<Array<MatchedRequirementColumn>>([]);
  const [disNext, setDisNext] = useState<boolean>(false);
  const [skipStock, setSkipStock] = useState<boolean>(false);
  const [matchFactory, setMatchFactory] = useState<boolean>(false);
  const [preOutDesc, setPreOutDesc] = useState<string>('');
  const [locTypes, setLocTypes] = useState<number[]>([1]);

  const scrollHeight = useMemo(() => {
    return (contentRef?.current?.getBoundingClientRect()?.height || 500) - 80;
  }, [contentRef?.current]);

  // 重置状态
  useEffect(() => {
    if (!open) {
      setCurrent(0);
      setSelectedProducts([]);
      setMatchedProducts([]);
      setDisNext(false);
      setPreOutDesc('');
      setLocTypes([1]);
    }
  }, [open]);

  const hasPreOutItems = () => matchedProducts.some(i => i.preOutNum && i.preOutNum > 0 && i.product.preReady === 0);

  const renderPreOutControls = () => {
    if (!hasPreOutItems()) return null;

    return (
      <>
        <Form.Item
          label={t('requirement.matchLocType')}
          style={{ marginBottom: 2 }}
        >
          <SelectLocationType
            mode="multiple"
            style={{ flex: 1 }}
            value={locTypes}
            maxTagCount={2}
          />
        </Form.Item>
        <Form.Item label={t('requirement.preOutDesc')} style={{ marginBottom: 2 }}>
          <Input.TextArea
            size="small"
            autoSize={{ minRows: 1, maxRows: 1 }}
            maxLength={200}
            value={preOutDesc}
            onChange={e => setPreOutDesc(e.target.value)}
          />
        </Form.Item>
      </>
    );
  };

  // 转换为 RequirementColumn 格式供 MatchStock 使用
  const convertToRequirementColumns = (): RequirementColumn[] => {
    return selectedProducts.map(p => ({
      product: p,
      reqs: [],
      useRepl: RequirementAddItemUseRepl.NOT_USE,
      bomItemId: '',
      bomReplId: '',
      bomRequireCount: p.quantity || 1,
      extCount: 0,
      baseQuantity: p.bomAmount || 0,
      attritionRate: 0,
      useQuantity: p.quantity || 1,
      useAmount: p.quantity || 1,
    }));
  };

  // 转换 RequirementAddItem 格式
  const buildAddItems = (): Array<RequirementAddItem> => {
    return matchedProducts
      .filter(item => item.useAmount > 0)
      .map((item) => {
        const baseQuantityNumber = roundToDecimal(Number(item?.baseQuantity) || 0);
        return {
          productId: item.product.id,
          bomItemId: item.bomItemId || '',
          bomReplId: item.bomReplId || '',
          useRepl: item.useRepl ?? RequirementAddItemUseRepl.NOT_USE,
          maxEstimatedPriceId: '',
          minEstimatedPriceId: '',
          maxEstimatedCost: '',
          minEstimatedCost: '',
          useAmount: item.useAmount.toString(),
          desc: '',
          purCount: (item.purCount ?? 0).toString(),
          puredCount: '0',
          outCount: '0',
          extCount: item.extCount.toString(),
          supplierId: '',
          ert: null,
          attritionRate: (item.attritionRate ?? 0).toString(),
          baseCount: baseQuantityNumber === 0 ? '' : baseQuantityNumber.toString(),
          matchKey: item.matchKey,
          bomAmount: baseQuantityNumber === 0 ? '' : baseQuantityNumber.toString(),
          useFactoryCount: item.useFactoryCount,
        };
      });
  };

  // 第一步：选择物料的表格列
  const step1Columns: ColumnsType<ProductWithNum> = [
    {
      title: t("purchase.seq"),
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: t("product.code"),
      dataIndex: "code",
      width: 120,
      render: (text) => <SText text={text || '-'} />,
    },
    {
      title: t("product.name"),
      dataIndex: "name",
      width: 150,
      render: (text) => <SText text={text || '-'} />,
    },
    {
      title: t("product.brand1"),
      dataIndex: "model",
      width: 250,
      render: (text) => <SText text={text || '-'} />,
    },
    {
      title: t("product.uom"),
      dataIndex: "uom",
      width: 80,
      render: (text) => <SText text={text || '-'} />,
    },
    {
      title: t("order.quantity"),
      dataIndex: "quantity",
      width: 120,
      render: (value, record, index) => (
        <InputNumber changeOnWheel
          formatter={val => numFormmater(Number(val || 0))}
          min={0}
          precision={0}
          value={value}
          onChange={(val) => {
            const newProducts = [...selectedProducts];
            newProducts[index].quantity = val ?? 0;
            setSelectedProducts(newProducts);
          }}
        />
      ),
    },
    {
      title: t("requirement.bomAmount"),
      dataIndex: "bomAmount",
      width: 120,
      render: (value, record, index) => (
        <InputNumber changeOnWheel
          formatter={val => numFormmater(Number(val || 0))}
          min={0}
          value={value}
          onChange={(val) => {
            const newProducts = [...selectedProducts];
            newProducts[index].bomAmount = val ?? 0;
            setSelectedProducts(newProducts);
          }}
        />
      ),
    },
  ];

  const steps = [
    {
      title: t("requirement.selectPro"),
      content: (
        <Flex vertical gap={12} style={{ height: '100%' }}>
          <Flex gap={6} align="center">
            <Button type="primary" onClick={() => setOpenProductSelect(true)}>
              {t("requirement.selectPro")}
            </Button>
            <Checkbox checked={skipStock} onChange={(e) => {
              let checked = e.target.checked;
              if (checked) {
                setSkipStock(true);
                setMatchFactory(false);
                setLocTypes([1])
              }
              else setSkipStock(false);
            }}>{t('requirement.skipStock')}</Checkbox>
            <Checkbox checked={matchFactory} disabled={skipStock} onChange={(e) => setMatchFactory(e.target.checked)}>{t('requirement.matchFactory')}</Checkbox>
          </Flex>
          <Form.Item required={!skipStock} label={t('requirement.matchLocType')} style={{ marginBottom: 4 }} >
            <SelectLocationType disabled={skipStock} value={locTypes} onChange={setLocTypes} mode="multiple" maxTagCount={2} />
          </Form.Item>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <CommonTable
              columns={step1Columns}
              dataSource={selectedProducts}
              rowKey="id"
              virtual
              pagination={false}
              bordered={false}
              scroll={{ y: scrollHeight }}
              size="small"
            />
          </div>
        </Flex>
      ),
    },
    {
      title: t("requirement.matchInventory"),
      content: (
        <MatchStock
          purchaseProducts={convertToRequirementColumns()}
          matchedProducts={matchedProducts}
          setMatchedProducts={setMatchedProducts}
          requireCount={1}
          scrollHeight={scrollHeight}
          setDisNext={setDisNext}
          matchFactory={matchFactory}
          locTypes={locTypes}
        />
      ),
    },
    {
      title: t("requirement.confirmAddProduct"),
      content: (
        <>
          {renderPreOutControls()}
          <Tabs
            type="card"
            items={[
              {
                label: <SText text={t('counting.addItem')} />,
                key: 'requirementList',
                children: <PreOutData
                  setMatchedProducts={setMatchedProducts}
                  confirmProducts={matchedProducts.filter(item => item.useAmount > 0)}
                  scrollHeight={scrollHeight - (hasPreOutItems() ? 68 : 0)}
                  confirmed
                />
              },
              {
                label: <SText text={t('requirement.preOutInventory')} />,
                key: 'preOutInventory',
                disabled: skipStock,
                children: <PreOutData
                  confirmProducts={matchedProducts.filter(i => i.preOutNum && i.preOutNum > 0 && i.product.preReady === 0)}
                  scrollHeight={scrollHeight - (hasPreOutItems() ? 68 : 0)}
                />
              },
            ]}
          />
        </>
      ),
    },
  ];

  const next = () => {
    if (current === 0) {
      if (selectedProducts.find(i => !!!i.quantity || !!!i.bomAmount)) {
        notification.error({
          placement: 'top',
          message: t('common.tip'),
          description: t('requirement.quantityAndBomAmount'),
        });
        return;
      }

      if (!skipStock && locTypes.length === 0) {
        notification.warning({
          placement: 'top',
          message: t('common.selectLeastOne'),
          description: t('requirement.matchLocType'),
        });
        return;
      }

      // 如果勾选了跳过库存匹配，直接设置 matchedProducts 并跳到确认步骤
      if (skipStock) {
        const matched: MatchedRequirementColumn[] = selectedProducts.map(p => ({
          product: p,
          reqs: [],
          useRepl: RequirementAddItemUseRepl.NOT_USE,
          bomItemId: '',
          bomReplId: '',
          bomRequireCount: p.quantity || 0,
          extCount: 0,
          attritionRate: 0,
          useQuantity: p.quantity || 0,
          useAmount: p.quantity || 0,
          stockNum: 0,
          stockPreOut: 0,
          useNum: 0,
          allocated: 0,
          purCount: p.preReady === 1 ? 0 : (p.quantity || 0),
          preOutNum: 0,
          preIn: 0,
          matchKey: '',
          useFactoryCount: 0,
          factoryCount: 0,
          factoryAllocated: 0,
          baseQuantity: p.bomAmount || 0,
        }));
        setMatchedProducts(matched);
        setCurrent(2); // 直接跳到确认步骤
      } else {
        setCurrent(current + 1); // 进入库存匹配步骤
      }
    } else if (current === 1) {
      // 验证是否有匹配的产品
      if (matchedProducts.length > 0 && matchedProducts.some(p => p.useAmount > 0)) {
        setCurrent(current + 1);
      } else {
        notification.error({
          placement: 'top',
          message: t('common.tip'),
          description: t('bom.errTipdesc'),
        });
      }
    } else if (current === 2) {
      handleConfirm();
    }
  };

  const prev = () => {
    if (current === 2 && skipStock) {
      setCurrent(0); // 如果跳过库存匹配，从确认步骤直接回到第一步
    } else if (current > 0) {
      setCurrent(current - 1);
    }
  };

  const handleConfirm = async () => {
    const addItems = buildAddItems();
    if (addItems.length === 0) {
      notification.error({
        placement: 'top',
        message: t('common.tip'),
        description: t('bom.errTipdesc'),
      });
      return;
    }

    setLoading(true);

    try {
      // 先判断是否有需要预出库的商品，避免用户取消库位类型后仍新增物料。
      const preOutItems = matchedProducts.filter(i => i.preOutNum && i.preOutNum > 0 && i.product.preReady === 0);
      // 第一步：执行 itemAdds 添加商品
      const res = await itemAdds(reqId, addItems);
      const newItems: RequirementDetailType[] = res.map(r => ({
        ...r,
        purchaseDesc: '',
        ertDate: '',
        outCount: '',
        checked: false,
      }));

      let updatedOrder: OrderV2 | undefined;
      let createdOrder: OrderV2 | undefined;

      if (preOutItems.length > 0 && locTypes.length > 0) {
        // 有需要预出库的商品，获取出库单列表
        const ordersRes = await inventoryOrders(reqId);

        // 查找未确认的预出库单
        const notConfirmOrder = ordersRes?.body?.find(order =>
          order.confirmState === ConfirmedEnum.NOTCONFIRM
        );

        const orderItems: OrderAddItemV2[] = preOutItems.map(item => ({
          sourceType: SourceTypeEnum.REQUIREMENT_ITEM,
          sourceId: newItems.find(ni => ni.productId === item.product.id)?.id ?? '',
          productId: item.product.id,
          count: item.preOutNum,
          lot: '',
          thdlot: '',
          desc: '',
          locations: [],
        }));

        if (notConfirmOrder) {
          // 有未确认的出库单，执行 orderItemAdds
          await orderItemAdds(notConfirmOrder.id, orderItems);
          if (preOutDesc) {
            await orderDescUpdate(notConfirmOrder.id, preOutDesc);
          }
          updatedOrder = await orderPreOut(notConfirmOrder.id, locTypes);
        } else {
          // 没有未确认的出库单，执行 orderAdd
          const order: OrderAddV2 = {
            arrivalAt: null,
            preTime: null,
            type: ChangeEnum.PRE_OUT,
            opType: OperateEnum.PICKING,
            desc: preOutDesc,
            items: orderItems,
          };
          const newOrder = await orderAdd(order);
          createdOrder = await orderPreOut(newOrder.id, locTypes);
        }
      }
      const factoryOut: Record<string, number> = {};
      newItems.forEach(r => {
        if (!!factoryOut[r.productId]) factoryOut[r.productId] -= (Number(r.useFactoryCount) || 0)
        else factoryOut[r.productId] = 0 - (Number(r.useFactoryCount) || 0)
      });
      const facInfo: AddFactoryInventory = {
        sourceType: FactoryInventorySourceType.REQUIREMENT,
        sourceId: reqId,
        items: Object.entries(factoryOut).map(([productId, count]) => ({ productId, count })).filter(i => i.count !== 0),
        desc: ''
      };
      if (facInfo.items.length > 0) {
        await fInventoryAdjust(facInfo)
          .catch((error: any) => {
            let description = '';
            if (error?.code === 70873) {
              let product = matchedProducts.find(p => p.product.id === error?.extra?.productId)?.product!
              description = t('apiError.error31') + ': ' + product.code
            }
            notification.error({ message: t('warehouse.outFail'), description })
          })
      } notification.success({ placement: 'top', message: t('common.addSuccess') });

      // 回调
      onSuccess?.(
        newItems.map(nI => ({
          ...nI,
          product: selectedProducts.find(pro => pro.id === nI.productId) || null
        })),
        {
          updatedOrder,
          createdOrder,
        }
      );

      // 重置并关闭
      setOpen(false);
    } catch (err: any) {
      if (err?.code === 73010) {
        notification.error({
          message: t('inventory.proQtyFalse'),
          description: err?.extra?.map((i: { productId: string }) => i.productId)?.join(', ')
        })
      } else if ([73022, 73014].includes(err)) {
        notification.error({
          message: t('inventory.calcErr')
        })
      } else if ([73024, 73028].includes(err?.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: err?.extra?.productCode
        })
      }
      notification.error({ placement: 'top', message: errorCodes[err] || t('common.addError') });
    } finally {
      setLoading(false);
    }
  };

  const items = steps.map((item) => ({
    key: item.title,
    title: item.title,
  }));

  return (
    <>
      <Modal
        onCancel={() => {
          if (current === steps.length - 1) {
            setOpen(false);
          } else {
            modal.confirm({
              title: t('common.tip'),
              content: t('requirement.notAddCloseTip'),
              onOk: () => {
                setOpen(false);
              },
            });
          }
        }}
        open={open}
        title={t('common.add')}
        centered
        footer={null}
        width="90vw"
      >
        <Steps current={current} items={items} style={{ marginBottom: 16 }} />
        <div
          ref={contentRef}
          style={{
            minHeight: 400,
            maxHeight: 'calc(100vh - 250px)',
            overflow: 'auto',
          }}
        >
          {steps[current].content}
        </div>
        <Flex style={{ marginTop: 24 }} gap={8}>
          <Button type="primary" loading={loading || disNext} onClick={() => next()}>
            {current === 2 ? t('common.confirm') : t('requirement.next')}
          </Button>
          {current !== 0 && (
            <Button onClick={() => prev()}>
              {t('requirement.previous')}
            </Button>
          )}
        </Flex>
      </Modal>

      {/* ProductSelectMulti 内嵌 Modal */}
      {openProductSelect && <ProductSelectMulti
        values={selectedProducts}
        handleOk={(products) => {
          // 过滤掉已存在的物料
          const existingIds = selectedProducts.map(p => p.id);
          const newProducts = products.filter(p => !existingIds.includes(p.id));
          setSelectedProducts([...selectedProducts, ...newProducts]);
        }}
        openProduct={openProductSelect}
        setOpenProduct={setOpenProductSelect}
      />}
    </>
  );
};

export default AddItem;
